import { abbreviateNumber, formatAvgRating } from '@lib/utils'
import { BagIcon } from '@components/icons/BagIcon'
import { BookShelfIcon } from '@components/icons/BookShelfIcon'
import { ChatboxIcon } from '@components/icons/ChatboxIcon'
import { EyeIcon } from '@components/icons/EyeIcon'
import { HeartClipboardIcon } from '@components/icons/HeartClipboardIcon'
import { StarSolidIcon } from '@components/icons/StarSolidIcon'
import SelectPerPage from '@components/Pagination/components/SelectPerPage/SelectPerPage'
import { Pagination } from '@components/Pagination'
import { BookEnum } from '@interfaces/BookEnum'
import { Loading } from '@components/Loading'
import Image from 'next/image'
import { Fragment } from 'react'
import { SearchForm } from './components/SearchForm'
import { MyEbookTabProps } from './interface'

const perPageOptions = [
  {
    label: '10',
    value: 10,
  },
  {
    label: '20',
    value: 20,
  },
  {
    label: '30',
    value: 30,
  },
]

export function MyEbookTab({
  initialValues,
  total,
  myEbookList,
  isLoading,
  page,
  perpage,
  pageChange,
  perpageChange,
  handleSubmit,
  handleViewDetail,
}: MyEbookTabProps) {
  return (
    <>
      <div className='grid gap-y-[20px]'>
        <hr className='w-full border-t border-gray-3 dark:border-purple' />
        <SearchForm initialValues={initialValues} handleSubmit={handleSubmit} />
        <hr className='w-full border-t border-gray-3 dark:border-purple' />
      </div>

      <div className='flex justify-between mobile:flex-col mt-[20px] mb-[10px]'>
        <div className='flex text-secondary-1 font-light space-x-[5px] text-[14px] mobile:mb-[10px]'>
          <p>จำนวนงานเขียน :</p>
          <p className='font-bold text-secondary dark:text-dark-10'>
            {total.toLocaleString()}
          </p>
          <p>รายการ</p>
        </div>
        <SelectPerPage
          value={perpage}
          options={perPageOptions}
          handleSelect={perpageChange}
          className='mobile:w-full'
        />
      </div>
      {isLoading ? (
        <div className='flex justify-center pt-[50px]'>
          <Loading size={50} />
        </div>
      ) : total === 0 ? (
        <div className='text-[24px] text-center font-medium text-gray-12 font-mitr my-[50px]'>
          ไม่พบอีบุ๊ก
        </div>
      ) : (
        myEbookList.map(item => (
          <Fragment key={item.id}>
            <div
              onClick={() => handleViewDetail(item.id)}
              className='flex items-center w-full cursor-pointer desktop-tablet:h-[150px] mobile:items-start mobile:mb-[10px]'
            >
              <div className='relative w-[92px] mobile:w-[57px] h-[129.38px] mobile:h-[80.16px] bg-gray-3 rounded-[8px] overflow-hidden shrink-0'>
                {item.coverImgPath ? (
                  <Image
                    src={item.coverImgPath}
                    alt='coverImgPath'
                    layout='fill'
                  />
                ) : (
                  <div className='absolute inset-0 bg-gray-3' />
                )}
              </div>
              <div className='ml-[20px] mobile:ml-[18px] mobile:py-0 w-full space-y-[20px] mobile:space-y-[10px]'>
                <div className='flex text-secondary dark:text-dark-10 text-[14px] justify-between'>
                  <span>
                    {item.bookType === BookEnum.MANGA
                      ? 'อีบุ๊กการ์ตูน'
                      : 'อีบุ๊กนิยาย'}
                  </span>
                  <div className='flex bg-white items-center justify-center disabled:opacity-30 border border-gray-3 rounded-lg min-w-[30px] h-[30px] text-secondary-1'>
                    <EyeIcon width='20' height='20' />
                  </div>
                </div>
                <div className='font-mitr text-secondary text-[16px] font-medium'>
                  {item.title}
                </div>
                <div className='grid grid-cols-[80px_80px_80px_80px_80px] gap-[30px] text-secondary-1 mobile:hidden'>
                  <div className='flex'>
                    <BookShelfIcon className='text-primary mr-[5px]' />
                    {abbreviateNumber(item.totalEbookSeries)}
                  </div>
                  <div className='flex'>
                    <BagIcon className='text-primary mr-[5px]' />
                    {abbreviateNumber(item.totalEbookPurchaseCount)}
                  </div>
                  <div className='flex'>
                    <StarSolidIcon className='text-primary mr-[5px]' />
                    {formatAvgRating(item.avgEbookRating)}
                  </div>
                  <div className='flex'>
                    <ChatboxIcon className='text-primary mr-[5px]' />
                    {abbreviateNumber(item.totalEbookReview)}
                  </div>
                  <div className='flex'>
                    <HeartClipboardIcon className='mr-[5px]' />
                    {abbreviateNumber(item.totalEbookWishlist)}
                  </div>
                </div>
              </div>
            </div>

            <hr className='w-full border-t border-gray-3 dark:border-purple mobile:hidden' />

            <div className='grid grid-cols-3 items-center font-light gap-y-[10px] desktop-tablet:hidden'>
              <div className='flex items-center'>
                <BookShelfIcon className='text-primary mr-[5px]' />
                {abbreviateNumber(item.totalEbookSeries)}
              </div>
              <div className='flex items-center'>
                <BagIcon className='text-primary mr-[5px]' />
                {abbreviateNumber(item.totalEbookPurchaseCount)}
              </div>
              <div className='flex items-center'>
                <StarSolidIcon className='text-primary mr-[5px]' />
                {item.avgEbookRating}
              </div>
              <div className='flex items-center'>
                <ChatboxIcon className='text-primary mr-[5px]' />
                {abbreviateNumber(item.totalEbookReview)}
              </div>
              <div className='flex items-center'>
                <HeartClipboardIcon className='mr-[5px]' />
                {abbreviateNumber(item.totalEbookWishlist)}
              </div>
            </div>
            <hr className='w-full border-t border-gray-3 dark:border-purple desktop-tablet:hidden my-[20px]' />
          </Fragment>
        ))
      )}
      <div className='desktop-tablet:mt-[30px]'>
        <Pagination
          page={page}
          perpage={perpage}
          total={total}
          pageChange={pageChange}
        />
      </div>
    </>
  )
}
